"use client";

import type { BaseComponentProps } from "@json-render/react";
import { useStateValue } from "@json-render/react";
import { Trans } from "@lingui/react/macro";
import {
  type Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { normalizeVizExportFormats } from "./chart-format";
import {
  buildVizDatasetFieldsPath,
  buildVizFieldMetaMap,
  isPercentFieldKey,
  parseVizDatasetFields,
} from "./dataset-fields";
import {
  copyVizAsPng,
  downloadRowsAsCsv,
  downloadRowsAsJson,
  downloadVizAsPng,
} from "./export-utils";
import {
  VizActionBar,
  VizColumnsMenu,
  type VizExportFormat,
  VizExportMenu,
} from "./viz-actions";
import {
  areColumnPinningStatesEqual,
  areSortingStatesEqual,
  areVisibilityStatesEqual,
  buildInitialColumnVisibility,
  clampTableLimit,
  normalizeColumnPinningState,
  normalizeColumnSizingConfig,
  normalizeRowPinningState,
  resolveInitialSortingState,
} from "./viz-table-state";

export type VizTableProps = BaseComponentProps<{
  dataPath: string;
  columns?: string[] | null;
  limit?: number | null;
  columnLabels?: Record<string, string> | null;
  defaultSort?: { key: string; direction: "asc" | "desc" } | null;
  allowColumnVisibilityToggle?: boolean | null;
  defaultVisibleColumns?: string[] | null;
  allowExport?: boolean | null;
  exportFormats?: string[] | null;
  enableMultiSort?: boolean | null;
  columnPinning?: { left?: string[] | null; right?: string[] | null } | null;
  rowPinning?: {
    top?: number[] | null;
    bottom?: number[] | null;
  } | null;
  columnSizing?: Record<
    string,
    {
      size?: number | null;
      minSize?: number | null;
      maxSize?: number | null;
    } | null
  > | null;
}>;

type ColumnType = "number" | "date" | "string";
type TableRow = Record<string, unknown>;

const EMPTY_DATA: unknown[] = [];
const MAX_CELL_LENGTH = 80;
const NUMBER_FORMATTER = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 3,
});
const PERCENT_FORMATTER = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 1,
});
const DEFAULT_EXPORT_FORMATS: VizExportFormat[] = ["csv", "json"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function truncateCell(value: string): string {
  if (value.length <= MAX_CELL_LENGTH) return value;
  return `${value.slice(0, MAX_CELL_LENGTH)}...`;
}

function removeCommas(value: string): string {
  return value.split(",").join("");
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(removeCommas(value));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function coerceDateEpoch(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveColumnType(args: {
  key: string;
  rows: TableRow[];
  fieldType?: string | null;
}): ColumnType {
  if (args.fieldType === "number") return "number";
  if (args.fieldType === "date") return "date";

  for (const row of args.rows) {
    const value = row[args.key];
    if (value === null || value === undefined || value === "") continue;
    if (coerceNumber(value) !== null) return "number";
    if (coerceDateEpoch(value) !== null) return "date";
    return "string";
  }

  return "string";
}

export function formatVizTableCell(
  value: unknown,
  options: { isPercent?: boolean; treatAsNumber?: boolean } = {},
): { text: string; title?: string } {
  if (value === null || value === undefined) return { text: "-" };

  const numeric =
    typeof value === "number"
      ? value
      : options.treatAsNumber
        ? coerceNumber(value)
        : null;

  if (numeric !== null) {
    try {
      return {
        text: (options.isPercent ? PERCENT_FORMATTER : NUMBER_FORMATTER).format(
          numeric,
        ),
      };
    } catch {
      return { text: String(value) };
    }
  }

  const raw = String(value);
  const trimmed = raw.trim();
  if (trimmed.length <= MAX_CELL_LENGTH) return { text: trimmed };
  return { text: truncateCell(trimmed), title: trimmed };
}

function normalizeRows(rows: unknown[]): TableRow[] {
  return rows.filter(isRecord).map((row) => ({ ...row }));
}

function getColumnStyle(args: {
  column: Column<TableRow>;
  isHeader: boolean;
}): CSSProperties {
  const minSize = args.column.columnDef.minSize;
  const maxSize = args.column.columnDef.maxSize;
  const baseStyle: CSSProperties = {
    width: `${args.column.getSize()}px`,
    minWidth: typeof minSize === "number" ? `${minSize}px` : undefined,
    maxWidth: typeof maxSize === "number" ? `${maxSize}px` : undefined,
  };

  const pinState = args.column.getIsPinned();
  if (pinState !== "left" && pinState !== "right") return baseStyle;

  const isLastLeft = pinState === "left" && args.column.getIsLastColumn("left");
  const isFirstRight =
    pinState === "right" && args.column.getIsFirstColumn("right");

  return {
    ...baseStyle,
    position: "sticky",
    left: pinState === "left" ? `${args.column.getStart("left")}px` : undefined,
    right:
      pinState === "right" ? `${args.column.getAfter("right")}px` : undefined,
    zIndex: args.isHeader ? 20 : 10,
    backgroundColor: args.isHeader ? "var(--muted)" : "var(--background)",
    borderRight: isLastLeft ? "1px solid var(--border)" : undefined,
    borderLeft: isFirstRight ? "1px solid var(--border)" : undefined,
  };
}

export function VizTable({ props }: VizTableProps) {
  const {
    dataPath,
    columns,
    limit,
    columnLabels,
    defaultSort,
    allowColumnVisibilityToggle,
    defaultVisibleColumns,
    allowExport,
    exportFormats,
    enableMultiSort,
    columnPinning,
    rowPinning,
    columnSizing,
  } = props;

  const fieldsPath = useMemo(
    () => buildVizDatasetFieldsPath(dataPath),
    [dataPath],
  );
  const rawRows = useStateValue<unknown>(dataPath);
  const rawFields = useStateValue<unknown>(fieldsPath);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const rows = useMemo(
    () => normalizeRows(Array.isArray(rawRows) ? rawRows : EMPTY_DATA),
    [rawRows],
  );
  const parsedFields = useMemo(
    () => parseVizDatasetFields(rawFields),
    [rawFields],
  );
  const metaByName = useMemo(
    () => buildVizFieldMetaMap(parsedFields),
    [parsedFields],
  );

  const resolvedColumns = useMemo(() => {
    const preferred = Array.isArray(columns)
      ? columns.filter(
          (key): key is string => typeof key === "string" && key.length > 0,
        )
      : [];
    if (preferred.length > 0) return preferred;

    if (parsedFields.length > 0) {
      return parsedFields.map((field) => field.name);
    }

    const firstRow = rows[0];
    return firstRow ? Object.keys(firstRow).sort() : [];
  }, [columns, parsedFields, rows]);

  const fieldTypeByName = useMemo(
    () => new Map(parsedFields.map((field) => [field.name, field.type])),
    [parsedFields],
  );

  const columnTypeByName = useMemo(() => {
    return new Map(
      resolvedColumns.map((key) => [
        key,
        resolveColumnType({
          key,
          rows,
          fieldType: fieldTypeByName.get(key) ?? null,
        }),
      ]),
    );
  }, [fieldTypeByName, resolvedColumns, rows]);

  const fieldDefaultVisibleMap = useMemo(() => {
    return new Map(
      parsedFields.map((field) => [field.name, field.defaultVisible]),
    );
  }, [parsedFields]);

  const [sorting, setSorting] = useState<SortingState>(() =>
    resolveInitialSortingState({
      columns: resolvedColumns,
      columnTypeByName,
      defaultSort,
    }),
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () =>
      buildInitialColumnVisibility({
        columns: resolvedColumns,
        defaultVisibleColumns,
        fieldDefaultVisibleMap,
      }),
  );
  const initialColumnPinning = useMemo(
    () =>
      normalizeColumnPinningState({
        columns: resolvedColumns,
        columnPinning,
      }),
    [columnPinning, resolvedColumns],
  );
  const [tableColumnPinning, setTableColumnPinning] = useState(
    () => initialColumnPinning,
  );
  const normalizedRowPinning = useMemo(
    () =>
      normalizeRowPinningState({
        rowCount: rows.length,
        rowPinning,
      }),
    [rowPinning, rows.length],
  );
  const normalizedColumnSizing = useMemo(
    () =>
      normalizeColumnSizingConfig({
        columns: resolvedColumns,
        columnSizing,
      }),
    [columnSizing, resolvedColumns],
  );

  useEffect(() => {
    setSorting((current) => {
      const valid = current.filter((entry) =>
        resolvedColumns.includes(entry.id),
      );
      const fallback = resolveInitialSortingState({
        columns: resolvedColumns,
        columnTypeByName,
        defaultSort,
      });
      const next = valid.length > 0 ? valid : fallback;
      return areSortingStatesEqual(next, current) ? current : next;
    });
  }, [columnTypeByName, defaultSort, resolvedColumns]);

  useEffect(() => {
    setColumnVisibility((current) => {
      const next: VisibilityState = {};
      for (const key of resolvedColumns) {
        if (current[key] === false) {
          next[key] = false;
        }
      }
      return areVisibilityStatesEqual(next, current) ? current : next;
    });
  }, [resolvedColumns]);

  useEffect(() => {
    setTableColumnPinning((current) =>
      areColumnPinningStatesEqual(current, initialColumnPinning)
        ? current
        : initialColumnPinning,
    );
  }, [initialColumnPinning]);

  const columnDefs = useMemo<ColumnDef<TableRow>[]>(() => {
    return resolvedColumns.map((key) => {
      const isPercent = isPercentFieldKey(key, metaByName);
      const isNumeric = columnTypeByName.get(key) === "number";
      const fieldMeta = metaByName.get(key);
      const headerLabel =
        columnLabels?.[key]?.trim() || fieldMeta?.label || key;
      const columnSize = normalizedColumnSizing[key];

      return {
        id: key,
        accessorKey: key,
        size: columnSize?.size,
        minSize: columnSize?.minSize,
        maxSize: columnSize?.maxSize,
        enableHiding: allowColumnVisibilityToggle !== false,
        enableSorting: true,
        sortingFn: (rowA, rowB, columnId) => {
          const type = columnTypeByName.get(columnId) ?? "string";
          const aValue = rowA.getValue(columnId as keyof TableRow);
          const bValue = rowB.getValue(columnId as keyof TableRow);

          if (type === "number") {
            const aNumber = coerceNumber(aValue);
            const bNumber = coerceNumber(bValue);
            if (aNumber === null && bNumber === null) return 0;
            if (aNumber === null) return 1;
            if (bNumber === null) return -1;
            return aNumber - bNumber;
          }

          if (type === "date") {
            const aDate = coerceDateEpoch(aValue);
            const bDate = coerceDateEpoch(bValue);
            if (aDate === null && bDate === null) return 0;
            if (aDate === null) return 1;
            if (bDate === null) return -1;
            return aDate - bDate;
          }

          const aText =
            aValue === null || aValue === undefined ? "" : String(aValue);
          const bText =
            bValue === null || bValue === undefined ? "" : String(bValue);
          return aText.localeCompare(bText);
        },
        header: ({ column }) => {
          const sorted = column.getIsSorted();
          return (
            <button
              type="button"
              className="inline-flex cursor-pointer items-center gap-1 rounded-sm px-0.5 py-0.5 text-foreground hover:text-foreground/90"
              onClick={column.getToggleSortingHandler()}
            >
              <span>{headerLabel}</span>
              {sorted === "asc" ? (
                <ArrowUpIcon className="size-3" />
              ) : sorted === "desc" ? (
                <ArrowDownIcon className="size-3" />
              ) : null}
            </button>
          );
        },
        cell: ({ row }) => {
          const cell = formatVizTableCell(row.original[key], {
            isPercent,
            treatAsNumber: isNumeric,
          });
          return (
            <div className="truncate" title={cell.title}>
              {cell.text}
            </div>
          );
        },
        meta: {
          isNumeric,
          columnType: columnTypeByName.get(key) ?? "string",
        },
      } satisfies ColumnDef<TableRow>;
    });
  }, [
    allowColumnVisibilityToggle,
    columnLabels,
    columnTypeByName,
    metaByName,
    normalizedColumnSizing,
    resolvedColumns,
  ]);

  const tableState = useMemo(
    () => ({
      sorting,
      columnVisibility,
      columnPinning: tableColumnPinning,
    }),
    [columnVisibility, sorting, tableColumnPinning],
  );

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    state: tableState,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setTableColumnPinning,
    enableMultiSort: enableMultiSort !== false,
    enableColumnPinning: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const resolvedLimit = useMemo(() => clampTableLimit(limit), [limit]);
  const coreRows = table.getCoreRowModel().rows;
  const sortedRows = table.getSortedRowModel().rows;
  const pinnedRowIds = useMemo(() => {
    const topIds: string[] = [];
    const topIdSet = new Set<string>();

    for (const value of normalizedRowPinning.top ?? []) {
      const index = Number(value);
      if (!Number.isInteger(index) || index < 0 || index >= coreRows.length) {
        continue;
      }
      const row = coreRows[index] ?? null;
      if (!row || topIdSet.has(row.id)) continue;
      topIds.push(row.id);
      topIdSet.add(row.id);
    }

    const bottomIds: string[] = [];
    const bottomIdSet = new Set<string>();
    for (const value of normalizedRowPinning.bottom ?? []) {
      const index = Number(value);
      if (!Number.isInteger(index) || index < 0 || index >= coreRows.length) {
        continue;
      }
      const row = coreRows[index] ?? null;
      if (!row || topIdSet.has(row.id) || bottomIdSet.has(row.id)) continue;
      bottomIds.push(row.id);
      bottomIdSet.add(row.id);
    }

    return {
      topIds,
      bottomIds,
    };
  }, [coreRows, normalizedRowPinning.bottom, normalizedRowPinning.top]);

  const visibleRowsResult = useMemo(() => {
    const sortedRowById = new Map(sortedRows.map((row) => [row.id, row]));
    const topRows = pinnedRowIds.topIds
      .map((id) => sortedRowById.get(id) ?? null)
      .filter((row): row is (typeof sortedRows)[number] => row !== null);
    const bottomRows = pinnedRowIds.bottomIds
      .map((id) => sortedRowById.get(id) ?? null)
      .filter((row): row is (typeof sortedRows)[number] => row !== null);

    const pinnedIdSet = new Set([
      ...topRows.map((row) => row.id),
      ...bottomRows.map((row) => row.id),
    ]);
    const centerRows = sortedRows.filter((row) => !pinnedIdSet.has(row.id));
    const pinnedCount = topRows.length + bottomRows.length;

    if (pinnedCount >= resolvedLimit) {
      const topCount = Math.min(topRows.length, resolvedLimit);
      const remaining = resolvedLimit - topCount;
      const bottomCount = Math.min(bottomRows.length, remaining);
      const needsBottomReserve =
        bottomRows.length > 0 && bottomCount === 0 && topCount > 0;
      const limitedTopRows = needsBottomReserve
        ? topRows.slice(0, topCount - 1)
        : topRows.slice(0, topCount);
      const limitedBottomRows = needsBottomReserve
        ? bottomRows.slice(-1)
        : bottomRows.slice(-bottomCount);

      return {
        rows: [...limitedTopRows, ...limitedBottomRows],
        pinnedIdSet,
      };
    }

    const centerCount = Math.max(0, resolvedLimit - pinnedCount);
    const limitedCenterRows = centerRows.slice(0, centerCount);

    return {
      rows: [...topRows, ...limitedCenterRows, ...bottomRows],
      pinnedIdSet,
    };
  }, [pinnedRowIds.bottomIds, pinnedRowIds.topIds, resolvedLimit, sortedRows]);
  const visibleRows = visibleRowsResult.rows;

  const visibleColumnIds = table
    .getVisibleLeafColumns()
    .map((column) => column.id);

  const visibleColumnSet = useMemo(
    () => new Set(visibleColumnIds),
    [visibleColumnIds],
  );

  const exportColumnIds = useMemo(() => {
    return visibleColumnIds.length > 0 ? visibleColumnIds : resolvedColumns;
  }, [resolvedColumns, visibleColumnIds]);

  const exportRows = table.getRowModel().rows.map((row) => row.original);

  const columnMenuItems = useMemo(() => {
    return resolvedColumns.map((key) => ({
      key,
      label: columnLabels?.[key]?.trim() || metaByName.get(key)?.label || key,
    }));
  }, [columnLabels, metaByName, resolvedColumns]);

  const resolvedAllowExport = allowExport !== false;
  const resolvedExportFormats = normalizeVizExportFormats(
    exportFormats,
    DEFAULT_EXPORT_FORMATS,
  );

  const handleExport = useCallback(
    async (format: VizExportFormat) => {
      if (format === "csv") {
        downloadRowsAsCsv({
          rows: exportRows,
          columns: exportColumnIds,
          baseName: "viz-table",
        });
        return;
      }

      if (format === "json") {
        downloadRowsAsJson({
          rows: exportRows,
          columns: exportColumnIds,
          baseName: "viz-table",
        });
        return;
      }

      if (format === "png") {
        await downloadVizAsPng({
          element: containerRef.current,
          baseName: "viz-table",
        });
      }
    },
    [exportColumnIds, exportRows],
  );
  const handleCopyImage = useCallback(async () => {
    await copyVizAsPng({
      element: containerRef.current,
    });
  }, []);

  if (resolvedColumns.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        <Trans>No columns available for this table.</Trans>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full">
      <VizActionBar>
        {allowColumnVisibilityToggle !== false ? (
          <VizColumnsMenu
            columns={columnMenuItems}
            visibleKeySet={visibleColumnSet}
            onToggle={(key, checked) => {
              table.getColumn(key)?.toggleVisibility(checked);
            }}
          />
        ) : null}
        {resolvedAllowExport ? (
          <VizExportMenu
            formats={resolvedExportFormats}
            onExport={handleExport}
            onCopyImage={handleCopyImage}
          />
        ) : null}
      </VizActionBar>

      <div className="w-full max-h-80 overflow-auto">
        <table className="w-full min-w-max text-[13px] leading-5">
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-border/80">
                {headerGroup.headers.map((header) => {
                  const isNumeric =
                    (
                      header.column.columnDef.meta as
                        | { isNumeric?: boolean }
                        | undefined
                    )?.isNumeric ?? false;
                  const columnStyle = getColumnStyle({
                    column: header.column,
                    isHeader: true,
                  });

                  return (
                    <th
                      key={header.id}
                      style={columnStyle}
                      className={[
                        "bg-muted/95 px-3 py-2.5 text-left text-[11px] font-semibold tracking-[0.08em] text-muted-foreground whitespace-nowrap",
                        isNumeric ? "text-right" : "",
                      ].join(" ")}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr
                key={row.id}
                className={[
                  "border-b border-border/80 last:border-b-0 hover:bg-muted/35",
                  row.index % 2 === 0 ? "bg-background" : "bg-muted/12",
                  visibleRowsResult.pinnedIdSet.has(row.id)
                    ? "bg-primary/[0.06]"
                    : "",
                ].join(" ")}
              >
                {row.getVisibleCells().map((cell) => {
                  const columnMeta = cell.column.columnDef.meta as
                    | {
                        isNumeric?: boolean;
                        columnType?: ColumnType;
                      }
                    | undefined;
                  const isNumeric = columnMeta?.isNumeric ?? false;
                  const isTextLike =
                    (columnMeta?.columnType ?? "string") === "string";
                  const columnStyle = getColumnStyle({
                    column: cell.column,
                    isHeader: false,
                  });

                  return (
                    <td
                      key={cell.id}
                      style={columnStyle}
                      className={[
                        "px-3 py-2.5 align-top",
                        isNumeric
                          ? "text-right font-medium tabular-nums whitespace-nowrap"
                          : isTextLike
                            ? "max-w-[22rem] whitespace-normal break-words text-left"
                            : "whitespace-nowrap text-left",
                      ].join(" ")}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
